// React
import { useEffect, useState } from "react";

// Third-Party
import { useSnackbar } from "notistack";

// Internal
import { RootState, useDispatch, useSelector } from "@redux/store";
import { clearMessages, setActiveConversation } from "@redux/slices/askDostChat";
import {
  deleteConversation,
  getConversations,
  renameConversation,
} from "../services/askDostService";

// Types
import { IConversation } from "../types";

interface IUseConversationHistoryProps {
  translate: (text: string, options?: any) => string;
  isOpen: boolean;
  onSelectConversation?: (conversationId: string, history?: any) => void;
}

export const useConversationHistory = ({
  translate,
  isOpen,
  onSelectConversation,
}: IUseConversationHistoryProps) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const activeConversationId = useSelector(
    (state: RootState) => state.askDostChat.activeConversationId,
  );
  const [search, setSearch] = useState("");
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingRenameConv, setPendingRenameConv] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    const handleChatCleared = () => {
      setSelectedConversationId(null);
    };

    window.addEventListener("chatCleared", handleChatCleared);
    return () => {
      window.removeEventListener("chatCleared", handleChatCleared);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchChats = async () => {
    setIsLoading(true);
    try {
      const res = await getConversations();
      const data = res?.data || res || [];
      setConversations(Array.isArray(data) ? data : data.conversations || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const chatTitle = conv.title || conv.name || conv.id || "";
    return chatTitle.toLowerCase().includes(search.toLowerCase());
  });

  const resetSearch = () => {
    setSearch("");
  };

  const handleSelect = (conv: IConversation) => {
    if (onSelectConversation && conv.id) {
      setSelectedConversationId(conv.id);
      onSelectConversation(conv.id, conv);
    }
    resetSearch();
  };

  const requestDelete = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    setPendingDeleteId(id);
  };

  const cancelDelete = () => setPendingDeleteId(null);

  const confirmDelete = async () => {
    const id = pendingDeleteId;
    if (!id) return;

    setIsDeleting(true);
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));

      if (selectedConversationId === id || !selectedConversationId) {
        setSelectedConversationId(null);
        dispatch(clearMessages());
        window.dispatchEvent(new Event("chatCleared"));
      }

      enqueueSnackbar(translate("chat_deleted_successfully"), {
        variant: "success",
      });
    } catch (error) {
      console.error("Error deleting chat:", error);
      enqueueSnackbar(translate("unable_to_delete_chat"), {
        variant: "error",
      });
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  };

  const requestRename = (event: React.MouseEvent, conv: IConversation) => {
    event.stopPropagation();
    setPendingRenameConv({ id: conv.id, title: conv.title || conv.name || "" });
  };

  const cancelRename = () => setPendingRenameConv(null);

  const confirmRename = async (newTitle: string) => {
    const conv = pendingRenameConv;
    if (!conv) return;

    setIsRenaming(true);
    try {
      await renameConversation(conv.id, newTitle);
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, title: newTitle } : c)),
      );

      if (activeConversationId === conv.id) {
        dispatch(setActiveConversation({ id: conv.id, title: newTitle }));
      }

      enqueueSnackbar(translate("chat_renamed_successfully"), {
        variant: "success",
      });
      setPendingRenameConv(null);
    } catch (error) {
      console.error("Error renaming chat:", error);
      enqueueSnackbar(translate("unable_to_rename_chat"), {
        variant: "error",
      });
    } finally {
      setIsRenaming(false);
    }
  };

  return {
    search,
    setSearch,
    isLoading,
    selectedConversationId,
    filteredConversations,
    resetSearch,
    handleSelect,
    pendingDeleteId,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
    pendingRenameConv,
    isRenaming,
    requestRename,
    cancelRename,
    confirmRename,
  };
};
